import { useState, useEffect, useMemo } from 'react';
import { CalendarCheck, ChevronLeft, ChevronRight, FileSpreadsheet, FileText, CheckCheck, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mecanicosApi } from '../lib/api';
import { fechaLima } from '../lib/fechas';

// Planilla de asistencia en cuadricula (tipo SENATI): filas = mecanicos activos, columnas =
// dias de la quincena (1-15 / 16-fin, igual que las planillas) o de la semana. Cada casilla se
// marca con un toque y rota: Presente -> Falta -> Estudio -> Descanso/Permiso -> vacio.
// 'ausente' (Falta) es lo unico que la planilla descuenta. 2026-10-09 (pedido de Milenko):
// Tardanza se cambio por Estudio (no descuenta, igual que Descanso); nunca se uso Tardanza.

type Estado = 'presente' | 'ausente' | 'estudio' | 'descanso';
const CICLO: (Estado | null)[] = ['presente', 'ausente', 'estudio', 'descanso', null];
const INFO: Record<Estado, { letra: string; nombre: string; clase: string }> = {
  presente: { letra: 'P', nombre: 'Presente', clase: 'bg-green-100 text-green-800 border-green-300' },
  ausente: { letra: 'F', nombre: 'Falta', clase: 'bg-red-100 text-red-700 border-red-300' },
  estudio: { letra: 'E', nombre: 'Estudio', clase: 'bg-violet-100 text-violet-800 border-violet-300' },
  descanso: { letra: 'D', nombre: 'Descanso / Permiso', clase: 'bg-sky-100 text-sky-800 border-sky-300' },
};
const DIAS = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const desdeIso = (s: string) => { const [a, m, d] = s.split('-').map(Number); return new Date(a, m - 1, d); };

function rango(modo: 'quincena' | 'semana', ref: string): { dias: string[]; titulo: string } {
  const r = desdeIso(ref);
  if (modo === 'quincena') {
    const a = r.getFullYear(), m = r.getMonth();
    const ini = r.getDate() <= 15 ? 1 : 16;
    const fin = r.getDate() <= 15 ? 15 : new Date(a, m + 1, 0).getDate();
    const dias = []; for (let d = ini; d <= fin; d++) dias.push(iso(new Date(a, m, d)));
    return { dias, titulo: `${ini === 1 ? '1ra' : '2da'} quincena de ${MESES[m]} ${a} (${ini}–${fin})` };
  }
  const lunes = new Date(r); lunes.setDate(r.getDate() - ((r.getDay() + 6) % 7));
  const dias = []; for (let i = 0; i < 7; i++) { const d = new Date(lunes); d.setDate(lunes.getDate() + i); dias.push(iso(d)); }
  const f = (s: string) => { const d = desdeIso(s); return `${d.getDate()} ${MESES[d.getMonth()].slice(0, 3)}`; };
  return { dias, titulo: `Semana del ${f(dias[0])} al ${f(dias[6])}` };
}

function mover(modo: 'quincena' | 'semana', ref: string, paso: number): string {
  const r = desdeIso(ref);
  if (modo === 'semana') { r.setDate(r.getDate() + 7 * paso); return iso(r); }
  if (paso > 0) return r.getDate() <= 15 ? iso(new Date(r.getFullYear(), r.getMonth(), 16)) : iso(new Date(r.getFullYear(), r.getMonth() + 1, 1));
  return r.getDate() <= 15 ? iso(new Date(r.getFullYear(), r.getMonth() - 1, 16)) : iso(new Date(r.getFullYear(), r.getMonth(), 1));
}

interface Mec { id: string; nombre: string; estado?: string; asistencias?: any }

// Observaciones opcionales por dia (2026-10-10, pedido de Milenko)
function mapaObs(raw: any): Record<string, string> {
  let arr: any[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === 'string') { try { arr = JSON.parse(raw) || []; } catch { arr = []; } }
  const m: Record<string, string> = {};
  arr.forEach((a) => { if (a && a.fecha && a.obs) m[String(a.fecha).slice(0, 10)] = String(a.obs); });
  return m;
}

function mapaAsistencias(raw: any): Record<string, Estado> {
  let arr: any[] = [];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === 'string') { try { arr = JSON.parse(raw) || []; } catch { arr = []; } }
  const m: Record<string, Estado> = {};
  arr.forEach((a) => { if (a && a.fecha) m[String(a.fecha).slice(0, 10)] = (a.estado || (a.presente ? 'presente' : 'ausente')) as Estado; });
  return m;
}

const Asistencia = () => {
  const hoy = fechaLima();
  const [mecanicos, setMecanicos] = useState<Mec[]>([]);
  const [marcas, setMarcas] = useState<Record<string, Record<string, Estado>>>({});
  const [cargando, setCargando] = useState(true);
  const [modo, setModo] = useState<'quincena' | 'semana'>('quincena');
  const [ref, setRef] = useState(hoy);
  const [guardando, setGuardando] = useState<Record<string, boolean>>({});
  const [obs, setObs] = useState<Record<string, Record<string, string>>>({});
  const [modoNota, setModoNota] = useState(false);

  useEffect(() => {
    mecanicosApi.getAll()
      .then((data) => {
        const activos = (data || []).filter((m: Mec) => (m.estado || 'activo') === 'activo')
          .sort((a: Mec, b: Mec) => a.nombre.trim().localeCompare(b.nombre.trim()));
        setMecanicos(activos);
        const mm: Record<string, Record<string, Estado>> = {};
        activos.forEach((m: Mec) => { mm[m.id] = mapaAsistencias(m.asistencias); });
        setMarcas(mm);
        const oo: Record<string, Record<string, string>> = {};
        activos.forEach((m: Mec) => { oo[m.id] = mapaObs(m.asistencias); });
        setObs(oo);
      })
      .catch(() => toast.error('No se pudo cargar la asistencia'))
      .finally(() => setCargando(false));
  }, []);

  const { dias, titulo } = useMemo(() => rango(modo, ref), [modo, ref]);

  async function marcar(mecId: string, fecha: string, estado: Estado | null) {
    const clave = `${mecId}|${fecha}`;
    const anterior = marcas[mecId]?.[fecha] ?? null;
    if (anterior === estado) return;
    setMarcas((p) => { const c = { ...(p[mecId] || {}) }; if (estado) c[fecha] = estado; else delete c[fecha]; return { ...p, [mecId]: c }; });
    setGuardando((g) => ({ ...g, [clave]: true }));
    try {
      await mecanicosApi.marcarAsistencia(mecId, { fecha, estado });
    } catch (e: any) {
      setMarcas((p) => { const c = { ...(p[mecId] || {}) }; if (anterior) c[fecha] = anterior; else delete c[fecha]; return { ...p, [mecId]: c }; });
      toast.error(e?.message || 'No se pudo guardar la marca');
    } finally {
      setGuardando((g) => { const n = { ...g }; delete n[clave]; return n; });
    }
  }

  async function editarObs(mecId: string, fecha: string) {
    const nombre = mecanicos.find((m) => m.id === mecId)?.nombre.trim() || '';
    if (!marcas[mecId]?.[fecha]) { toast.error('Primero marca la asistencia de ese día; después le agregas la observación'); return; }
    const actual = obs[mecId]?.[fecha] || '';
    const texto = window.prompt(`Observación de ${nombre} el ${desdeIso(fecha).toLocaleDateString('es-PE')} (déjalo vacío para borrarla):`, actual);
    if (texto === null) return;
    const limpio = texto.trim().slice(0, 200);
    if (limpio === actual) return;
    try {
      await mecanicosApi.obsAsistencia(mecId, { fecha, obs: limpio });
      setObs((p) => { const c = { ...(p[mecId] || {}) }; if (limpio) c[fecha] = limpio; else delete c[fecha]; return { ...p, [mecId]: c }; });
      toast.success(limpio ? 'Observación guardada' : 'Observación borrada');
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar la observación'); }
  }

  // Observaciones del periodo visible (para la lista, el Excel y el PDF)
  const obsPeriodo = () => {
    const out: [string, string, string, string][] = [];
    dias.forEach((d) => mecanicos.forEach((m) => {
      const o = obs[m.id]?.[d]; if (o) out.push([desdeIso(d).toLocaleDateString('es-PE'), m.nombre.trim(), marcas[m.id]?.[d] ? INFO[marcas[m.id][d]].nombre : '', o]);
    }));
    return out;
  };

  function rotar(mecId: string, fecha: string) {
    if (modoNota) { editarObs(mecId, fecha); return; }
    const actual = marcas[mecId]?.[fecha] ?? null;
    const sig = CICLO[(CICLO.indexOf(actual) + 1) % CICLO.length];
    marcar(mecId, fecha, sig);
  }

  async function todosPresentesHoy() {
    const pendientes = mecanicos.filter((m) => !marcas[m.id]?.[hoy]);
    if (!pendientes.length) { toast.info('Todos ya tienen marca hoy'); return; }
    await Promise.all(pendientes.map((m) => marcar(m.id, hoy, 'presente')));
    toast.success(`${pendientes.length} marcados como presentes hoy. Cambia a quienes faltaron, estudian o descansan.`);
  }

  const totales = (mecId: string) => {
    const t = { presente: 0, ausente: 0, estudio: 0, descanso: 0 } as Record<Estado, number>;
    dias.forEach((d) => { const e = marcas[mecId]?.[d]; if (e) t[e]++; });
    return t;
  };

  const etiquetaDia = (s: string) => { const d = desdeIso(s); return `${DIAS[d.getDay()]} ${d.getDate()}`; };

  function filasExport() {
    return mecanicos.map((m) => {
      const t = totales(m.id);
      return [m.nombre.trim(), ...dias.map((d) => (marcas[m.id]?.[d] ? INFO[marcas[m.id][d]].letra : '')), t.presente, t.ausente, t.estudio, t.descanso];
    });
  }
  const encabezado = () => ['Mecánico', ...dias.map(etiquetaDia), 'P', 'F', 'E', 'D'];

  function excel() {
    const aoa = [[`Asistencia — ${titulo}`], ['P = Presente · F = Falta · E = Estudio · D = Descanso/Permiso'], [], encabezado(), ...filasExport()];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 26 }, ...dias.map(() => ({ wch: 6 })), { wch: 4 }, { wch: 4 }, { wch: 4 }, { wch: 4 }];
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Asistencia');
    const ob = obsPeriodo();
    if (ob.length) {
      const ws2 = XLSX.utils.aoa_to_sheet([['Fecha', 'Mecánico', 'Marca', 'Observación'], ...ob]);
      ws2['!cols'] = [{ wch: 12 }, { wch: 26 }, { wch: 18 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Observaciones');
    }
    XLSX.writeFile(wb, `Asistencia_${dias[0]}_${dias[dias.length - 1]}.xlsx`);
  }

  function pdf() {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(13); doc.text('Asistencia de mecánicos', 14, 14);
    doc.setFontSize(10); doc.text(titulo, 14, 20);
    doc.setFontSize(8); doc.setTextColor(110); doc.text('P = Presente · F = Falta · E = Estudio · D = Descanso/Permiso', 14, 25); doc.setTextColor(0);
    const color: Record<string, [number, number, number]> = { P: [220, 245, 228], F: [253, 226, 226], T: [254, 243, 199], D: [224, 242, 254] };
    autoTable(doc, {
      startY: 29, head: [encabezado()], body: filasExport(),
      styles: { fontSize: 7, cellPadding: 1.2, halign: 'center' },
      columnStyles: { 0: { halign: 'left', cellWidth: 38 } },
      headStyles: { fillColor: [31, 61, 48] },
      didParseCell: (h) => { if (h.section === 'body' && h.column.index > 0 && h.column.index <= dias.length) { const c = color[String(h.cell.raw)]; if (c) h.cell.styles.fillColor = c; } },
    });
    const ob = obsPeriodo();
    if (ob.length) {
      autoTable(doc, { head: [['Fecha', 'Mecánico', 'Marca', 'Observación']], body: ob,
        styles: { fontSize: 8, cellPadding: 1.5 }, headStyles: { fillColor: [31, 61, 48] }, columnStyles: { 3: { cellWidth: 140 } } });
    }
    doc.save(`Asistencia_${dias[0]}_${dias[dias.length - 1]}.pdf`);
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <CalendarCheck className="w-6 h-6 text-gray-600" />
          <h1 className="text-2xl font-bold text-gray-800">Asistencia</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={todosPresentesHoy} className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg bg-green-600 hover:bg-green-700 text-white">
            <CheckCheck className="w-4 h-4" /> Marcar presentes hoy
          </button>
          <button onClick={() => setModoNota((v) => !v)} className={`flex items-center gap-1 px-3 py-2 text-sm rounded-lg border ${modoNota ? 'bg-amber-500 text-white border-amber-500' : 'bg-white hover:bg-gray-50'}`}
            title="Activa y toca una casilla ya marcada para escribir una observación">
            <StickyNote className="w-4 h-4" /> {modoNota ? 'Modo nota: ACTIVO' : 'Modo nota'}
          </button>
          <button onClick={excel} className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50"><FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel</button>
          <button onClick={pdf} className="flex items-center gap-1 px-3 py-2 text-sm rounded-lg border bg-white hover:bg-gray-50"><FileText className="w-4 h-4 text-red-600" /> PDF</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="inline-flex rounded-lg border overflow-hidden text-sm">
          {(['quincena', 'semana'] as const).map((m) => (
            <button key={m} onClick={() => setModo(m)} className={`px-3 py-1.5 ${modo === m ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}>{m === 'quincena' ? 'Quincena' : 'Semana'}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setRef(mover(modo, ref, -1))} className="p-1.5 rounded border bg-white" title="Anterior"><ChevronLeft className="w-4 h-4" /></button>
          <span className="font-semibold text-gray-800 px-2">{titulo}</span>
          <button onClick={() => setRef(mover(modo, ref, 1))} className="p-1.5 rounded border bg-white" title="Siguiente"><ChevronRight className="w-4 h-4" /></button>
          {!dias.includes(hoy) && <button onClick={() => setRef(hoy)} className="ml-1 text-sm text-blue-600 underline">Ir a hoy</button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs mb-3">
        {(Object.keys(INFO) as Estado[]).map((e) => (
          <span key={e} className={`px-2 py-0.5 rounded border ${INFO[e].clase}`}><b>{INFO[e].letra}</b> {INFO[e].nombre}</span>
        ))}
        <span className="text-gray-500">· Toca una casilla para cambiarla. Solo F (falta) se descuenta en la planilla. Con <b>Modo nota</b> activo, tocar una casilla marcada le agrega una observación (punto naranja).</span>
      </div>

      {cargando ? <p className="text-gray-500">Cargando...</p> : (
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="sticky left-0 z-10 bg-gray-50 text-left px-3 py-2 min-w-[180px] border-b">Mecánico</th>
                {dias.map((d) => {
                  const dom = desdeIso(d).getDay() === 0;
                  return (
                    <th key={d} className={`px-1 py-2 border-b text-center font-medium min-w-[40px] ${d === hoy ? 'bg-blue-600 text-white' : dom ? 'text-gray-400' : 'text-gray-600'}`}>
                      <div className="text-[10px] leading-none">{DIAS[desdeIso(d).getDay()]}</div>
                      <div>{desdeIso(d).getDate()}</div>
                    </th>
                  );
                })}
                {(['presente', 'ausente', 'estudio', 'descanso'] as Estado[]).map((e) => (
                  <th key={e} className="px-2 py-2 border-b text-center text-xs text-gray-600" title={INFO[e].nombre}>{INFO[e].letra}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mecanicos.map((m) => {
                const t = totales(m.id);
                return (
                  <tr key={m.id} className="border-b last:border-0">
                    <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-medium text-gray-800 whitespace-nowrap">{m.nombre.trim()}</td>
                    {dias.map((d) => {
                      const e = marcas[m.id]?.[d];
                      const futuro = d > hoy;
                      const ocupado = !!guardando[`${m.id}|${d}`];
                      return (
                        <td key={d} className={`p-0.5 text-center ${d === hoy ? 'bg-blue-50' : ''}`}>
                          <button
                            disabled={futuro}
                            onClick={() => rotar(m.id, d)}
                            title={futuro ? 'Día futuro' : (e ? INFO[e].nombre : 'Sin marcar') + (obs[m.id]?.[d] ? ` — Obs.: ${obs[m.id][d]}` : '')}
                            className={`relative w-9 h-8 rounded border text-xs font-bold ${modoNota && e ? 'ring-1 ring-amber-400 ' : ''}${e ? INFO[e].clase : 'border-gray-200 text-gray-300 hover:bg-gray-50'} ${futuro ? 'opacity-30 cursor-not-allowed' : ''} ${ocupado ? 'animate-pulse' : ''}`}
                          >
                            {e ? INFO[e].letra : '·'}
                            {obs[m.id]?.[d] && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-orange-500" />}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-2 text-center text-green-700 font-semibold">{t.presente || ''}</td>
                    <td className="px-2 text-center text-red-600 font-semibold">{t.ausente || ''}</td>
                    <td className="px-2 text-center text-violet-700 font-semibold">{t.estudio || ''}</td>
                    <td className="px-2 text-center text-sky-700 font-semibold">{t.descanso || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!cargando && obsPeriodo().length > 0 && (
        <div className="mt-4 bg-white rounded-lg shadow p-3">
          <div className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1"><StickyNote className="w-4 h-4 text-orange-500" /> Observaciones del periodo</div>
          <ul className="text-sm space-y-1">
            {obsPeriodo().map(([f, n, mk, o], i) => <li key={i}><span className="text-gray-500">{f}</span> · <b>{n}</b>{mk && <span className="text-gray-500"> ({mk})</span>}: {o}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Asistencia;
